import { Link } from "react-router-dom";
import { ArrowRight, Check, Award, Network, FileText, Users, Quote } from "lucide-react";
import { CONTACT, IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import FAQ from "@/components/site/FAQ";
import useSeo from "@/lib/useSeo";
import { useT, useLang } from "@/lib/i18n";

const PROFESSIONAL_FAQ_BI = [
  {
    q: { es: "¿Qué tipo de profesionales sanitarios pueden derivar pacientes a Método TAO?", ca: "Quin tipus de professionals sanitaris poden derivar pacients al Mètode TAO?" },
    a: {
      es: "Oncólogos, oncólogas, hematólogos, médicos rehabilitadores, psicooncólogos, psicólogos clínicos, fisioterapeutas oncológicos, enfermería de oncología, ginecólogos, internistas que tratan fibromialgia, y cualquier profesional sanitario que detecte en su consulta a una paciente que se beneficiaría de un acompañamiento cuerpo-mente complementario.",
      ca: "Oncòlegs, oncòlogues, hematòlegs, metges rehabilitadors, psicooncòlegs, psicòlegs clínics, fisioterapeutes oncològics, infermeria d'oncologia, ginecòlegs, internistes que tracten fibromiàlgia, i qualsevol professional sanitari que detecti a la seva consulta una pacient que es beneficiaria d'un acompanyament cos-ment complementari.",
    },
  },
  {
    q: { es: "¿Cómo se coordina el acompañamiento con el equipo médico de la paciente?", ca: "Com es coordina l'acompanyament amb l'equip mèdic de la pacient?" },
    a: {
      es: "Antes de la primera sesión, la paciente completa un cuestionario clínico detallado (diagnóstico, fase, tratamientos, port-a-cath, linfedema, neuropatía, medicación). Si el caso lo requiere, contacto directamente con el profesional derivante para validar contraindicaciones específicas. Las sesiones nunca interfieren con el plan terapéutico y siempre lo respetan como prioridad.",
      ca: "Abans de la primera sessió, la pacient completa un qüestionari clínic detallat (diagnòstic, fase, tractaments, port-a-cath, limfedema, neuropatia, medicació). Si el cas ho requereix, contacto directament amb el professional derivant per validar contraindicacions específiques. Les sessions mai interfereixen amb el pla terapèutic i sempre el respecten com a prioritat.",
    },
  },
  {
    q: { es: "¿En qué evidencia científica se basa el enfoque?", ca: "En quina evidència científica es basa l'enfocament?" },
    a: {
      es: "Las intervenciones cuerpo-mente que aplico están alineadas con las guías clínicas SIO-ASCO 2023 (ansiedad y depresión en cáncer), ASCO-SIO 2024 (manejo de fatiga oncológica) y NCCN nivel 1 de evidencia. Yoga restaurativo, mindfulness y prácticas basadas en regulación del sistema nervioso autónomo, todas con evidencia en RCTs publicados en Journal of Clinical Oncology y otras revistas de referencia.",
      ca: "Les intervencions cos-ment que aplico estan alineades amb les guies clíniques SIO-ASCO 2023 (ansietat i depressió en càncer), ASCO-SIO 2024 (gestió de fatiga oncològica) i NCCN nivell 1 d'evidència. Ioga restauratiu, mindfulness i pràctiques basades en regulació del sistema nerviós autònom, totes amb evidència en RCTs publicats al Journal of Clinical Oncology i altres revistes de referència.",
    },
  },
  {
    q: { es: "¿Tenéis acuerdos con seguros privados o instituciones?", ca: "Teniu acords amb assegurances privades o institucions?" },
    a: {
      es: "Trabajo de forma privada e individual con pacientes derivadas. Para acuerdos institucionales con clínicas privadas, hospitales privados o aseguradoras (Sanitas, Adeslas, DKV, Asisa, Mapfre, Mutua Madrileña, Caser…) podemos explorar fórmulas a medida: programas piloto, sesiones grupales para pacientes, formación a equipos. Contactad para evaluar el encaje.",
      ca: "Treballo de manera privada i individual amb pacients derivades. Per a acords institucionals amb clíniques privades, hospitals privats o asseguradores (Sanitas, Adeslas, DKV, Asisa, Mapfre, Mutua Madrileña, Caser…) podem explorar fórmules a mida: programes pilot, sessions grupals per a pacients, formació a equips. Contacteu per avaluar l'encaix.",
    },
  },
  {
    q: { es: "¿Existe un protocolo de comunicación de evolución al profesional derivante?", ca: "Existeix un protocol de comunicació d'evolució al professional derivant?" },
    a: {
      es: "Sí, si la paciente lo autoriza. Puedo enviar un breve informe inicial (objetivos del acompañamiento, frecuencia) y un informe periódico de evolución cada 3 meses si el profesional derivante lo solicita. Confidencialidad absoluta del contenido emocional, solo se comparten datos funcionales relevantes para la coordinación clínica.",
      ca: "Sí, si la pacient ho autoritza. Puc enviar un breu informe inicial (objectius de l'acompanyament, freqüència) i un informe periòdic d'evolució cada 3 mesos si el professional derivant ho sol·licita. Confidencialitat absoluta del contingut emocional, només es comparteixen dades funcionals rellevants per a la coordinació clínica.",
    },
  },
  {
    q: { es: "¿Cómo deriváis a una paciente?", ca: "Com deriveu una pacient?" },
    a: {
      es: "Lo más sencillo: dadle a la paciente esta web (metodo-tao.com) o el enlace de reserva directa. Ella reserva una primera videollamada gratuita de 15 minutos y se hace cargo de su proceso. Si preferís contacto directo profesional-profesional para casos delicados, escribid a marta@metodo-tao.com con el asunto 'Derivación profesional'.",
      ca: "El més senzill: doneu a la pacient aquesta web (metodo-tao.com) o l'enllaç de reserva directa. Ella reserva una primera videotrucada gratuïta de 15 minuts i es fa càrrec del seu procés. Si preferiu contacte directe professional-professional per a casos delicats, escriviu a marta@metodo-tao.com amb l'assumpte 'Derivació professional'.",
    },
  },
];

const ProfesionalesSanitarios = () => {
  const t = useT();
  const lang = useLang();
  useSeo({
    title: {
      es: "Para Profesionales Sanitarios · Derivación de Pacientes | Método TAO",
      ca: "Per a Professionals Sanitaris · Derivació de Pacients | Mètode TAO",
    },
    description: {
      es: "Información para oncólogos, psicooncólogos, fisioterapeutas y profesionales sanitarios sobre el acompañamiento cuerpo-mente Método TAO. Evidencia científica, coordinación clínica y derivación de pacientes con cáncer, fibromialgia o fatiga crónica.",
      ca: "Informació per a oncòlegs, psicooncòlegs, fisioterapeutes i professionals sanitaris sobre l'acompanyament cos-ment Mètode TAO. Evidència científica, coordinació clínica i derivació de pacients amb càncer, fibromiàlgia o fatiga crònica.",
    },
    path: "/profesionales",
  });

  const PROFESSIONAL_FAQ = PROFESSIONAL_FAQ_BI.map((it) => ({
    q: it.q[lang] ?? it.q.es,
    a: it.a[lang] ?? it.a.es,
  }));

  const credentials = [
    t("pro.creds.1"),
    t("pro.creds.2"),
    t("pro.creds.3"),
    t("pro.creds.4"),
    t("pro.creds.5"),
    t("pro.creds.6"),
  ];

  const useCases = [
    { icon: Users, title: t("pro.use1.t"), body: t("pro.use1.b") },
    { icon: Network, title: t("pro.use2.t"), body: t("pro.use2.b") },
    { icon: Award, title: t("pro.use3.t"), body: t("pro.use3.b") },
    { icon: FileText, title: t("pro.use4.t"), body: t("pro.use4.b") },
  ];

  const evidence = [
    { tag: t("pro.evidence.1.tag"), title: t("pro.evidence.1.title"), body: t("pro.evidence.1.body") },
    { tag: t("pro.evidence.2.tag"), title: t("pro.evidence.2.title"), body: t("pro.evidence.2.body") },
    { tag: t("pro.evidence.3.tag"), title: t("pro.evidence.3.title"), body: t("pro.evidence.3.body") },
  ];

  return (
    <div className="bg-ivory" data-testid="professionals-page">
      <section className="pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("pro.eyebrow")}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05]">
              {t("pro.title1")} <span className="italic text-terracotta">{t("pro.title2")}</span> {t("pro.title3")}
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
              {t("pro.lead")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:marta@metodo-tao.com?subject=Derivaci%C3%B3n%20profesional%20%C2%B7%20M%C3%A9todo%20TAO"
                data-testid="prof-cta-email"
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-7 py-4 text-sm font-medium hover:bg-terracotta transition-all"
              >
                {t("pro.btn.email")} <ArrowRight size={15} />
              </a>
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="prof-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 text-charcoal rounded-full px-7 py-4 text-sm font-medium hover:bg-charcoal hover:text-ivory transition-all"
              >
                {t("pro.btn.whatsapp")}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
              <img src={IMG.handOnShoulder} alt="Acompañamiento profesional cuerpo-mente · Método TAO" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-charcoal text-ivory grain">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-6">{t("pro.evidence.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] max-w-3xl">
              {t("pro.evidence.title1")} <span className="italic text-terracotta">{t("pro.evidence.title2")}</span>.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {evidence.map((e, i) => (
              <Reveal key={e.tag} delay={i * 0.08}>
                <article className="bg-white/[0.03] border border-ivory/10 rounded-2xl p-7 h-full">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-terracotta">{e.tag}</span>
                  <h3 className="mt-4 font-serif text-2xl leading-snug">{e.title}</h3>
                  <p className="mt-3 text-ivory/70 text-[14.5px] leading-relaxed">{e.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/blog/evidencia-cientifica-yoga-oncologico-mindfulness"
              data-testid="prof-evidence-link"
              className="inline-flex items-center gap-2 text-sm text-ivory border-b border-terracotta pb-1 hover:text-terracotta transition-colors"
            >
              {t("pro.evidence.link")} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("pro.use.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              {t("pro.use.title1")} <span className="italic">{t("pro.use.title2")}</span>.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6 lg:gap-7">
            {useCases.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <article className="bg-sand/30 rounded-3xl p-8 h-full border border-sand/60">
                  <span className="w-12 h-12 rounded-full bg-ivory flex items-center justify-center text-olive">
                    <c.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-charcoal leading-tight">{c.title}</h3>
                  <p className="mt-3 text-charcoal/70 leading-relaxed text-[15px]">{c.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-sand/30 grain">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("pro.creds.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              {t("pro.creds.title1")} <span className="italic">{t("pro.creds.title2")}</span>.
            </h2>
            <p className="mt-6 text-charcoal/70 leading-relaxed text-[16px]">
              {t("pro.creds.lead")}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <ul className="space-y-3">
              {credentials.map((c) => (
                <li key={c} className="flex items-start gap-3 py-3 border-b border-sand">
                  <Check size={16} className="text-terracotta shrink-0 mt-1.5" />
                  <span className="text-[15.5px] text-charcoal/85 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Quote size={28} className="mx-auto text-terracotta mb-8" />
            <p className="font-serif text-2xl md:text-3xl text-charcoal leading-[1.3] max-w-2xl mx-auto">
              {t("pro.institutional.quote1")} <span className="italic text-terracotta">{t("pro.institutional.quote2")}</span>.
            </p>
            <a
              href="mailto:marta@metodo-tao.com?subject=Acuerdo%20institucional%20%C2%B7%20M%C3%A9todo%20TAO"
              data-testid="prof-institutional-email"
              className="mt-10 inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-7 py-4 text-sm font-medium hover:bg-terracotta transition-all"
            >
              {t("pro.institutional.btn")} <ArrowRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>

      <FAQ
        eyebrow={t("pro.faq.eyebrow")}
        title={t("pro.faq.title")}
        items={PROFESSIONAL_FAQ}
      />

      <SectionCTA
        title={t("pro.cta.title")}
        body={t("pro.cta.body")}
        primaryLabel="marta@metodo-tao.com"
        primaryTo="mailto:marta@metodo-tao.com?subject=Profesional%20sanitario%20%C2%B7%20Consulta"
      />
    </div>
  );
};

export default ProfesionalesSanitarios;
